import { z } from 'zod';
import { openWorldExtractionReceiptSchema } from './extraction';
import { OPEN_WORLD_MAX_CARGO_SLOTS } from './field-cargo';
import { openWorldCoordinateSchema } from './grid';
import { OPEN_WORLD_SAFE_PORT_IDS } from './ports';

/**
 * **Como se entra no mundo aberto, e como se volta.**
 *
 * ## A porta, e porque ela é uma só
 *
 * Um piloto entra no mundo aberto a partir da caça, escolhendo um LADO — norte,
 * sul, este ou oeste — e mais nada. Não escolhe o porto, não escolhe a
 * coordenada, não escolhe o quadrante e não escolhe a nave com que entra: tudo
 * isso é derivado pelo servidor a partir do que ele já é.
 *
 * O lado é a única escolha porque é a única que significa alguma coisa para
 * quem está a olhar para um mapa que ainda não existe: "para norte" é uma
 * decisão; "para `A/3`" seria uma coordenada que ninguém tem como avaliar.
 *
 * ## O que o navegador nunca envia
 *
 * Perfil de combate, vida, joias, cartas, carga, ouro, `partyId`, lista de
 * integrantes, posição, quadrante ou porto. Nada disso tem campo nenhum aqui, e
 * é essa ausência — e não uma verificação — que o torna impossível.
 *
 * ## Uma conta, um sítio
 *
 * Caça, duelo e mundo aberto são três sítios e uma conta está num. A entrada
 * recusa-se quando a conta já está noutro, e a recusa tem nome.
 */

/**
 * A versão dos contratos de entrada. A deles, e de mais nada.
 *
 * **2 desde o gate V28I.AD**, e o degrau é uma recusa nova: `world_full`. Um
 * quadrante passou a ter lotação, portanto uma entrada passou a poder falhar
 * por uma razão que nenhum cliente anterior sabe desenhar — e desenhar uma
 * recusa desconhecida como um erro genérico seria dizer ao jogador que alguma
 * coisa se partiu quando o que aconteceu foi o mundo estar cheio.
 *
 * **3 desde o gate V28I.AE**, e o degrau é um campo e uma recusa. O campo é a
 * `cadence`: o mundo aberto passou a ter um teto de salvas por segundo, e um
 * painel que mostrasse 120/s enquanto a sala aplica 20/s estaria a mentir ao
 * jogador sobre a nave dele. A recusa é `world_misconfigured`, para uma
 * instalação ligada sem essa política declarada.
 *
 * **4 desde o gate V28I.BY**, e o degrau é o `lastReceipt`: o recibo da última
 * expedição acabada. Uma expedição fechada que ainda aparecia como ativa passa a
 * ser acabada pela autoridade quando a conta pergunta, e o piloto recebe o
 * desfecho dela em vez de um botão para a retomar.
 *
 * **5 desde a ADR 0140**, e o degrau é o `socketUrl`: um bilhete passa a poder
 * dizer que o socket se abre noutro sítio. Sobe em vez de o campo ser
 * opcional porque estes esquemas são `.strict()` — um cliente da versão 4 a ler
 * uma resposta com um campo a mais falharia a validação e ficaria sem entrada,
 * o que é pior do que ser recusado no aperto de mão com uma razão.
 */
export const OPEN_WORLD_ENTRY_VERSION = 5;

/** As quatro escolhas que um piloto tem. Um lado, e nunca uma coordenada. */
export const OPEN_WORLD_ENTRY_SIDES = ['north', 'south', 'east', 'west'] as const;
export type OpenWorldEntrySide = (typeof OPEN_WORLD_ENTRY_SIDES)[number];
export const openWorldEntrySideSchema = z.enum(OPEN_WORLD_ENTRY_SIDES);

/**
 * As rotas. Constantes, para que um erro de escrita seja um erro de compilação.
 *
 * A do socket é servida **antes** do Hono, como a do duelo: uma resposta `101`
 * carrega um socket vivo e só é válida emparelhada com o pedido exato que a
 * pediu, portanto nada a pode envolver, copiar ou reler.
 */
export const OPEN_WORLD_ENTRY_ROUTE = '/api/open-world/enter';
export const OPEN_WORLD_STATE_ROUTE = '/api/open-world/state';
export const OPEN_WORLD_TICKET_ROUTE = '/api/open-world/socket-ticket';
export const OPEN_WORLD_SOCKET_ROUTE = '/api/open-world/socket';

/**
 * **How long a minted ticket may be, as a string.**
 *
 * It was written twice as a bare `.min(16).max(2048)` and is now one fact, for
 * a reason that arrived with the direct transport: the host has to refuse an
 * oversized first message BEFORE parsing it, and the ceiling it refuses against
 * must be this one rather than a second number that happens to agree today.
 * See `BATTLE_TRANSPORT_MAX_ATTACH_BYTES`.
 *
 * The lower bound is not a format check - the signature is - it only keeps an
 * obviously empty string from reaching one.
 */
export const OPEN_WORLD_TICKET_MIN_LENGTH = 16;
export const OPEN_WORLD_TICKET_MAX_LENGTH = 2048;

/**
 * **Entrar, ou retomar.**
 *
 * O lado é obrigatório para uma entrada nova e **ignorado** numa retoma: quem
 * já tem uma expedição a decorrer volta para onde ela está, e deixá-lo escolher
 * um lado seria deixá-lo teletransportar-se.
 */
export const openWorldEntryRequestSchema = z
  .object({
    v: z.literal(OPEN_WORLD_ENTRY_VERSION),
    side: openWorldEntrySideSchema,
  })
  .strict();

export type OpenWorldEntryRequest = z.infer<typeof openWorldEntryRequestSchema>;

/** Porque não se pode entrar. Fechada, e cada uma é um facto do servidor. */
export const OPEN_WORLD_ENTRY_REFUSALS = [
  /** O mundo aberto não está ligado nesta instalação. */
  'unavailable',
  /** Está num duelo. Uma conta está num sítio. */
  'in_duel',
  /** Já está no mundo aberto. A resposta é retomar, não entrar outra vez. */
  'already_in_world',
  /** Há um recibo por ler. Acabe a expedição anterior primeiro. */
  'receipt_pending',
  /** A caça não pôde ser fechada em segurança. Nada mudou. */
  'hunt_busy',
  /**
   * **Os portos elegíveis daquele lado estão cheios** (gate V28I.AD).
   *
   * Não é um erro e não é permanente: a lotação de um quadrante é autoritativa,
   * e uma entrada que não caiba é recusada ANTES de a localização ser
   * reclamada. Nada foi criado, a caça continua fechada como já estava, e
   * tentar outra vez daqui a pouco é a resposta certa.
   */
  'world_full',
  /**
   * **A instalação está ligada e mal configurada** (gate V28I.AE).
   *
   * Falta o teto de salvas por segundo do mundo aberto, ou ele é inválido. A
   * entrada é recusada **antes de a caça fechar**, antes de qualquer vaga ser
   * reservada e antes de a localização se mover: nada acontece, e a razão tem
   * nome em vez de aparecer como uma avaria genérica.
   *
   * Não é um estado que um jogador possa provocar nem resolver — é uma variável
   * de ambiente por declarar, e quem a lê num registo é quem opera.
   */
  'world_misconfigured',
] as const;
export type OpenWorldEntryRefusal = (typeof OPEN_WORLD_ENTRY_REFUSALS)[number];

/**
 * **Onde esta conta está, do ponto de vista do mundo aberto.**
 *
 * Lida antes de mostrar o botão e outra vez ao abrir o painel. Diz três coisas:
 * se pode entrar, se já está lá dentro (e onde), e se há um recibo por
 * apresentar de uma expedição que acabou enquanto ninguém olhava.
 */
export const openWorldEntryStateSchema = z
  .object({
    v: z.literal(OPEN_WORLD_ENTRY_VERSION),
    /** Pode esta conta entrar agora? Falso não é um erro; é um estado. */
    canEnter: z.boolean(),
    /** Porque não, quando não. */
    refusal: z.enum(OPEN_WORLD_ENTRY_REFUSALS).nullable(),
    /**
     * A expedição em curso, ou `null`.
     *
     * Quando existe, a interface mostra **RETOMAR EXPEDIÇÃO** e não cria outra:
     * entrar duas vezes seria duas naves, e a autoridade da localização recusa
     * a segunda de qualquer maneira.
     */
    active: z
      .object({
        expeditionId: z.uuid(),
        worldId: z.uuid(),
        quadrant: openWorldCoordinateSchema,
        /** `A/3`, para se poder mostrar sem derivar nada no cliente. */
        label: z.string().min(2).max(8),
        /**
         * **Se o quadrante onde a conta está é um dos oito portos.**
         *
         * Sai de `isOpenWorldSafePort` e de mais lado nenhum, exatamente como
         * o campo homónimo da vista da PT: a segurança de um quadrante é uma
         * propriedade da grelha, e uma segunda tabela a dizê-lo seria uma
         * segunda resposta que mais cedo ou mais tarde discordava.
         *
         * Descreve o `quadrant` acima — a coordenada autoritativa — e não o
         * porto por onde se entrou. Durante uma travessia ambos continuam a ser
         * a ORIGEM até ao compromisso, que é onde o casco realmente está.
         */
        safe: z.boolean(),
      })
      .strict()
      .nullable(),
    /**
     * A capacidade que uma entrada nova teria, calculada agora.
     *
     * Mostrada **antes** da confirmação, porque é a coisa que decide quanto se
     * pode trazer de volta — e porque ela congela no instante em que a
     * expedição começa, portanto vê-la depois seria vê-la tarde.
     */
    capacity: z.number().int().min(0).max(OPEN_WORLD_MAX_CARGO_SLOTS),
    /**
     * **A cadência que esta nave vai realmente ter lá dentro** (gate V28I.AE).
     *
     * O mundo aberto tem um teto de salvas por segundo, e ele é uma política da
     * autoridade: mostrar a cadência do equipamento e aplicar outra seria mentir
     * ao jogador sobre a nave dele. Aqui vêm as duas, e um booleano que diz se a
     * segunda é a primeira.
     *
     * **Em milésimos de salva por segundo**, porque um número na rede é um
     * inteiro: 540,54 salvas por segundo viajam como `540540`. O que se
     * desenha é a divisão por mil, e o que atravessa nunca é uma vírgula
     * flutuante.
     *
     * `null` quando o mundo aberto não está configurado para o dizer. A
     * ausência de uma política é dita pela recusa, e não por um número inventado
     * aqui.
     */
    cadence: z
      .object({
        /** O que o equipamento desta conta pede, sem teto nenhum. */
        shipMilliPerSecond: z.number().int().min(0),
        /** O que a sala vai aplicar: `min(nave, teto do mundo)`. */
        effectiveMilliPerSecond: z.number().int().min(0),
        /** É o mundo que está a limitar esta nave? */
        limited: z.boolean(),
      })
      .strict()
      .nullable(),
    /** Um recibo por apresentar, de uma expedição que já acabou. */
    receiptPending: z.boolean(),
    /**
     * **O recibo da última expedição acabada** (versão 4, gate V28I.BY), quando
     * a conta não está no mundo; `null` quando nunca acabou nenhuma.
     *
     * Um recibo é uma linha que já existe, e repeti-lo não repete nada. É o que
     * o piloto recebe quando a resposta do fim se perdeu pelo caminho, e quando
     * a autoridade acabou de fechar uma expedição que ainda aparecia como ativa:
     * em vez de "Extração pronta." e silêncio, o desfecho verdadeiro.
     */
    lastReceipt: openWorldExtractionReceiptSchema.nullable(),
  })
  .strict();

export type OpenWorldEntryState = z.infer<typeof openWorldEntryStateSchema>;

/**
 * **O que uma entrada devolve**: onde se entrou, e a chave para lá chegar.
 *
 * O bilhete vem já nesta resposta para que a primeira ligação não precise de
 * uma segunda ida ao servidor. As seguintes — reconexões e travessias — pedem o
 * seu, porque um bilhete é de uso único.
 */
export const openWorldEntryResultSchema = z.discriminatedUnion('ok', [
  z
    .object({
      ok: z.literal(true),
      v: z.literal(OPEN_WORLD_ENTRY_VERSION),
      expeditionId: z.uuid(),
      worldId: z.uuid(),
      quadrant: openWorldCoordinateSchema,
      label: z.string().min(2).max(8),
      portId: z.enum(OPEN_WORLD_SAFE_PORT_IDS),
      /** A capacidade CONGELADA desta expedição. Não volta a ser calculada. */
      capacity: z.number().int().min(0).max(OPEN_WORLD_MAX_CARGO_SLOTS),
      /** Retomada em vez de criada. A interface diz uma coisa diferente. */
      resumed: z.boolean(),
      ticket: z.string().min(OPEN_WORLD_TICKET_MIN_LENGTH).max(OPEN_WORLD_TICKET_MAX_LENGTH),
      /**
       * **Onde abrir o socket** (versão 5). `null` é o caminho de sempre.
       *
       * Vem já aqui pela mesma razão que o bilhete vem: a primeira ligação não
       * deve precisar de uma segunda ida ao servidor. E, como o bilhete, é o
       * SERVIDOR que o escolhe.
       */
      socketUrl: z.string().min(6).max(256).nullable(),
    })
    .strict(),
  z.object({ ok: z.literal(false), refusal: z.enum(OPEN_WORLD_ENTRY_REFUSALS) }).strict(),
]);

export type OpenWorldEntryResult = z.infer<typeof openWorldEntryResultSchema>;

/**
 * **Um bilhete para uma sala.**
 *
 * O cliente diz para que quadrante quer o bilhete, e o servidor verifica que é
 * onde a conta está — ou o vizinho para onde uma travessia já foi autorizada.
 * Pedir um bilhete não move ninguém e não decide nada: é o coordenador que diz
 * onde a conta está, e este pedido só pode confirmar.
 */
export const openWorldTicketRequestSchema = z
  .object({
    v: z.literal(OPEN_WORLD_ENTRY_VERSION),
    quadrant: openWorldCoordinateSchema,
  })
  .strict();

export const openWorldTicketResultSchema = z.discriminatedUnion('ok', [
  z
    .object({
      ok: z.literal(true),
      v: z.literal(OPEN_WORLD_ENTRY_VERSION),
      ticket: z.string().min(OPEN_WORLD_TICKET_MIN_LENGTH).max(OPEN_WORLD_TICKET_MAX_LENGTH),
      /** Quantos milissegundos ele vale. Curto de propósito. */
      ttlMs: z.number().int().min(0),
      /**
       * **Onde abrir o socket, quando não é aqui** (versão 5, ADR 0140).
       *
       * `null` é o caminho de sempre: o Durable Object deste mesmo domínio, e o
       * cliente não precisa de saber que existe uma alternativa. Uma cadeia
       * quando um host de batalha detém o quadrante — e é o SERVIDOR que a
       * escolhe, nunca o cliente: um destino que o navegador pudesse propor
       * seria um jogador a escolher a sua própria autoridade.
       *
       * O bilhete NÃO vai aqui dentro. Ele vem ao lado, e viaja na primeira
       * mensagem do socket, porque uma URL entra em logs de proxy, no histórico
       * e nas métricas por rota.
       */
      socketUrl: z.string().min(6).max(256).nullable(),
    })
    .strict(),
  z
    .object({
      ok: z.literal(false),
      refusal: z.enum(['unavailable', 'wrong_quadrant', 'not_in_world']),
    })
    .strict(),
]);

export type OpenWorldTicketResult = z.infer<typeof openWorldTicketResultSchema>;

/**
 * O rótulo de um quadrante: linha em letra, coluna em número. `A/3`.
 *
 * Aqui e não no cliente porque é a **apresentação** de uma coordenada, e uma
 * apresentação derivada em dois sítios é duas apresentações no dia em que uma
 * delas mudar. O servidor manda-a pronta; o cliente mostra-a.
 */
export function openWorldQuadrantLabel(coordinate: {
  readonly rowIndex: number;
  readonly columnIndex: number;
}): string {
  return `${String.fromCharCode(65 + coordinate.rowIndex)}/${coordinate.columnIndex + 1}`;
}
